<template>
  <div class="bg-white border border-gray-200 rounded-lg p-5 mb-6">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 class="text-sm font-semibold text-gray-700">Levy-Jennings Chart</h3>
      <div class="flex items-center gap-4 text-xs text-gray-500">
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#16a34a"></span>Accepted
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#d97706"></span>Warning (1₂s)
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#dc2626"></span>Rejected
        </span>
      </div>
    </div>

    <div v-if="sortedResults.length === 0"
      class="flex items-center justify-center h-32 text-gray-400 text-sm border border-dashed border-gray-200 rounded">
      No results to plot yet
    </div>

    <div v-else class="relative select-none" ref="wrap" @mouseleave="tooltip = null">
      <svg :viewBox="`0 0 ${W} ${H}`" width="100%" class="overflow-visible">

        <!-- Zone fills (±1SD green, ±2SD yellow, ±3SD rose) -->
        <rect :x="PAD.l" :y="yPx(mean + sd)"    :width="plotW" :height="yPx(mean - sd)    - yPx(mean + sd)"    fill="#f0fdf4" />
        <rect :x="PAD.l" :y="yPx(mean + 2*sd)"  :width="plotW" :height="yPx(mean + sd)    - yPx(mean + 2*sd)"  fill="#fefce8" />
        <rect :x="PAD.l" :y="yPx(mean - sd)"    :width="plotW" :height="yPx(mean - 2*sd)  - yPx(mean - sd)"    fill="#fefce8" />
        <rect :x="PAD.l" :y="yPx(mean + 3*sd)"  :width="plotW" :height="yPx(mean + 2*sd)  - yPx(mean + 3*sd)"  fill="#fff7f7" />
        <rect :x="PAD.l" :y="yPx(mean - 2*sd)"  :width="plotW" :height="yPx(mean - 3*sd)  - yPx(mean - 2*sd)"  fill="#fff7f7" />

        <!-- Reference lines -->
        <line v-for="ref in yRefs" :key="`ref-${ref.label}`"
          :x1="PAD.l" :x2="PAD.l + plotW"
          :y1="yPx(ref.value)" :y2="yPx(ref.value)"
          :stroke="ref.stroke" :stroke-width="ref.sw"
          :stroke-dasharray="ref.dash" />

        <!-- SD labels (left) -->
        <text v-for="ref in yRefs" :key="`ll-${ref.label}`"
          :x="PAD.l - 6" :y="yPx(ref.value) + 4"
          text-anchor="end" font-size="9"
          :fill="ref.label === 'Mean' ? '#374151' : '#9ca3af'"
          :font-weight="ref.label === 'Mean' ? '600' : '400'">
          {{ ref.label }}
        </text>

        <!-- Actual values (right) -->
        <text v-for="ref in yRefs" :key="`lr-${ref.label}`"
          :x="PAD.l + plotW + 5" :y="yPx(ref.value) + 4"
          text-anchor="start" font-size="8"
          :fill="ref.label === 'Mean' ? '#374151' : '#9ca3af'">
          {{ ref.value.toFixed(3) }}
        </text>

        <!-- Connecting line -->
        <polyline v-if="sortedResults.length > 1"
          :points="linePoints"
          fill="none" stroke="#d1d5db" stroke-width="1.5"
          stroke-linejoin="round" stroke-linecap="round" />

        <!-- Data points -->
        <g v-for="(r, i) in sortedResults" :key="r.id || i">
          <!-- White surface ring -->
          <circle :cx="xPx(i)" :cy="yPx(r.value)" r="5.5" fill="white" />
          <!-- Status dot -->
          <circle :cx="xPx(i)" :cy="yPx(r.value)" r="4" :fill="pointColor(r)" />
          <!-- Invisible hit target (≥12px radius for easy hover) -->
          <circle :cx="xPx(i)" :cy="yPx(r.value)" r="12"
            fill="transparent" style="cursor:pointer"
            @mouseenter.stop="onHover(r, i, $event)" />
        </g>

        <!-- X-axis date labels -->
        <text v-for="lbl in xLabels" :key="`x-${lbl.i}`"
          :x="xPx(lbl.i)" :y="H - PAD.b + 14"
          text-anchor="middle" fill="#9ca3af" font-size="9">
          {{ lbl.label }}
        </text>

        <!-- Axis rules -->
        <line :x1="PAD.l" :x2="PAD.l"           :y1="PAD.t"     :y2="H - PAD.b" stroke="#e5e7eb" stroke-width="1" />
        <line :x1="PAD.l" :x2="PAD.l + plotW"    :y1="H - PAD.b" :y2="H - PAD.b" stroke="#e5e7eb" stroke-width="1" />
      </svg>

      <!-- Hover tooltip -->
      <div v-if="tooltip"
        class="absolute z-20 pointer-events-none bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2.5 text-xs"
        :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }">
        <div class="font-semibold text-gray-800 mb-1.5">
          {{ tooltip.run_date }}
          <span v-if="tooltip.shift" class="font-normal text-gray-400"> · {{ tooltip.shift }}</span>
        </div>
        <div class="flex gap-4 mb-1.5">
          <span><span class="text-gray-400">Value </span><span class="font-mono font-medium">{{ tooltip.value }}</span></span>
          <span><span class="text-gray-400">z = </span>
            <span class="font-mono" :class="zClass(tooltip.value)">{{ zScore(tooltip.value) }}</span>
          </span>
        </div>
        <div class="flex items-center gap-1 flex-wrap">
          <span class="w-2 h-2 rounded-full shrink-0" :style="{ background: pointColor(tooltip) }"></span>
          <span class="font-medium" :class="tooltip.accepted ? 'text-green-700' : 'text-red-700'">
            {{ tooltip.accepted ? 'Accepted' : 'Rejected' }}
          </span>
          <span v-for="f in (tooltip.westgard_flags || [])" :key="f"
            class="px-1 py-0.5 rounded font-mono text-[10px]"
            :class="f === '1_2s' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'">
            {{ f }}
          </span>
        </div>
        <div v-if="tooltip.comment" class="mt-1 text-gray-400 italic max-w-[180px] truncate">{{ tooltip.comment }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LevyJenningsChart',
  props: {
    lot:     { type: Object, required: true },
    results: { type: Array,  default: () => [] },
  },
  data() {
    return {
      W:       600,
      H:       260,
      PAD:     { t: 16, r: 54, b: 32, l: 42 },
      tooltip: null,
    }
  },
  computed: {
    mean()  { return parseFloat(this.lot?.mean) || 0 },
    sd()    { return parseFloat(this.lot?.sd)   || 1 },
    plotW() { return this.W - this.PAD.l - this.PAD.r },
    plotH() { return this.H - this.PAD.t - this.PAD.b },
    yMin()  { return this.mean - 3.6 * this.sd },
    yMax()  { return this.mean + 3.6 * this.sd },
    sortedResults() {
      return [...this.results].sort((a, b) => (a.run_date < b.run_date ? -1 : 1))
    },
    linePoints() {
      return this.sortedResults
        .map((r, i) => `${this.xPx(i)},${this.yPx(r.value)}`)
        .join(' ')
    },
    yRefs() {
      const { mean, sd } = this
      return [
        { value: mean + 3*sd, label: '+3SD', stroke: '#fca5a5', sw: 1,   dash: '3 3' },
        { value: mean + 2*sd, label: '+2SD', stroke: '#fde68a', sw: 1,   dash: '3 3' },
        { value: mean + sd,   label: '+1SD', stroke: '#d1d5db', sw: 1,   dash: '3 3' },
        { value: mean,        label: 'Mean', stroke: '#374151', sw: 1.5, dash: '' },
        { value: mean - sd,   label: '−1SD', stroke: '#d1d5db', sw: 1,   dash: '3 3' },
        { value: mean - 2*sd, label: '−2SD', stroke: '#fde68a', sw: 1,   dash: '3 3' },
        { value: mean - 3*sd, label: '−3SD', stroke: '#fca5a5', sw: 1,   dash: '3 3' },
      ]
    },
    xLabels() {
      const n = this.sortedResults.length
      if (n === 0) return []
      const step = Math.max(1, Math.ceil(n / 8))
      const out  = []
      for (let i = 0; i < n; i += step)
        out.push({ i, label: this.fmtDate(this.sortedResults[i].run_date) })
      if (out[out.length - 1]?.i !== n - 1)
        out.push({ i: n - 1, label: this.fmtDate(this.sortedResults[n - 1].run_date) })
      return out
    },
  },
  methods: {
    yPx(v) {
      return this.PAD.t + (1 - (parseFloat(v) - this.yMin) / (this.yMax - this.yMin)) * this.plotH
    },
    xPx(i) {
      const n = this.sortedResults.length
      return n <= 1
        ? this.PAD.l + this.plotW / 2
        : this.PAD.l + (i / (n - 1)) * this.plotW
    },
    pointColor(r) {
      if (!r.accepted) return '#dc2626'
      if (r.westgard_flags?.includes('1_2s')) return '#d97706'
      return '#16a34a'
    },
    zScore(v) {
      if (!this.sd) return '—'
      return ((parseFloat(v) - this.mean) / this.sd).toFixed(2)
    },
    zClass(v) {
      const z = Math.abs(parseFloat(this.zScore(v)) || 0)
      if (z > 3) return 'text-red-600 font-semibold'
      if (z > 2) return 'text-amber-600'
      return 'text-gray-700'
    },
    fmtDate(s) {
      if (!s) return ''
      return new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    },
    onHover(r, i) {
      const wrap     = this.$refs.wrap
      const wrapRect = wrap.getBoundingClientRect()
      const svgRect  = wrap.querySelector('svg').getBoundingClientRect()
      const sx = svgRect.width  / this.W
      const sy = svgRect.height / this.H
      const px = this.xPx(i) * sx + (svgRect.left - wrapRect.left)
      const py = this.yPx(parseFloat(r.value)) * sy + (svgRect.top  - wrapRect.top)
      const tipW = 200
      const left = px + 14 + tipW > wrapRect.width ? px - tipW - 14 : px + 14
      this.tooltip   = { ...r, left, top: Math.max(0, py - 35) }
    },
  },
}
</script>
